import { ClientBase } from "./base.js";
import { ApiError } from "../errors.js";
import { MosqueClient } from "./mosque.js";
/** 409 punch_sequence from the timeclock; `allowed` lists the kinds valid right now. */
export class PunchSequenceError extends ApiError {
    allowed;
    constructor(message, allowed) {
        super(409, "punch_sequence", message);
        this.name = "PunchSequenceError";
        this.allowed = allowed;
    }
}
/** Dine client (#1084): guest table ordering, cash settle, kitchen line. */
export class DineClient extends MosqueClient {
    /** Public. A device (kiosk/board/kitchen) asks for a pairing code; staff bind it in the portal. */
    async startEdgeClaim(firmwareVersion = "", role) {
        return this.request("POST", "/v1/edge/claim", role ? { firmwareVersion, role } : { firmwareVersion });
    }
    /** Public, poll-secret gated. 204 -> pending; 200 -> claimed (device token delivered once). */
    async pollEdgeClaim(claimId, pollToken) {
        const res = await this.request("GET", `/v1/edge/claim/${encodeURIComponent(claimId)}/poll`, undefined, false, false, { "X-Lumora-Claim-Poll": pollToken });
        if (!res)
            return { status: "pending" };
        return {
            status: "claimed",
            deviceId: res.deviceId,
            token: res.token,
            ...(res.role ? { role: res.role } : {}),
            ...(res.tenantSlug ? { tenantSlug: res.tenantSlug } : {}),
            ...(res.locationId ? { locationId: res.locationId } : {}),
        };
    }
    /** Device bearer (#1198): which tenant/branch/role this paired screen is. 401 when revoked. */
    async edgeWhoAmI(deviceToken) {
        return this.request("GET", "/v1/edge/whoami", undefined, false, false, {
            Authorization: `Bearer ${deviceToken}`,
        });
    }
    /** Public (#1198). 404 `dine_config_missing` until the owner saves one via PUT /content. */
    async getDineConfig(slug) {
        return this.request("GET", `${this.siteBase(slug)}/dine-config`);
    }
    /** Public. The token travels in the body, never the URL. 404 for unknown/rotated/revoked. */
    async resolveTableTag(token) {
        return this.request("POST", "/v1/dining/tags/resolve", { token });
    }
    /** Public. Starts (201) or joins (200, joined:true) the anonymous table session. */
    async startTableSession(token) {
        return this.request("POST", "/v1/dining/tags/session", { token });
    }
    /**
     * POSTs the existing checkout route with `payCash: true`; answers with the
     * backend CashPlacementJSON shape (cashCode, expiresAt).
     */
    async placeCashOrder(slug, input, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/checkout`, { ...input, payCash: true }, false, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Staff (orders fulfill/manage). 404 cash_code_invalid, 429 rate_limited. */
    async settleCash(code) {
        return this.request("POST", "/v1/orders/settle-cash", { code }, true);
    }
    /** Staff (orders:fulfill). Queues a drawer kick on the paired printer. 404 other tenant, 409 no_printer. */
    async kickDrawer(orderId) {
        await this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/drawer-kick`, undefined, true);
    }
    /** Staff (orders:fulfill). Queues a cash receipt print. 404 other tenant, 409 no_printer. */
    async printReceipt(orderId) {
        await this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/print-receipt`, undefined, true);
    }
    /**
     * Staff POS (kitchen auth + orders fulfill/manage). The server prices the order.
     * Pass `idempotencyKey` so a double-submit returns the same order. `pay: "card"` answers
     * 501 `not_implemented`; invalid items/modifiers/caps answer 400.
     */
    async placePosOrder(input, opts = {}) {
        return this.request("POST", `/v1/orders/pos${ClientBase.query({ location: opts.location })}`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Staff POS menu with modifier groups, options and price deltas (display only). */
    async getPosCatalog(location) {
        return this.request("GET", `/v1/orders/pos/catalog${ClientBase.query({ location })}`, undefined, true);
    }
    async getKitchenLineStages(location) {
        return this.request("GET", `/v1/kitchen/line/stages${ClientBase.query({ location })}`, undefined, true);
    }
    async listKitchenLineTickets(opts = {}) {
        return this.request("GET", `/v1/kitchen/line/tickets${ClientBase.query({
            location: opts.location,
            include_ready: opts.includeReady ? "1" : undefined,
        })}`, undefined, true);
    }
    /** `fromStage` guards against a ticket that already moved (409 conflict). */
    async bumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "bump", opts);
    }
    async unbumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "unbump", opts);
    }
    async markKitchenTicketReady(orderId, opts = {}) {
        return this.kitchenAct(orderId, "ready", opts);
    }
    kitchenAct(orderId, action, opts) {
        const body = {};
        if (opts.fromStage)
            body["from_stage"] = opts.fromStage;
        if (opts.location !== undefined)
            body["location"] = opts.location;
        return this.request("POST", `/v1/kitchen/line/tickets/${encodeURIComponent(orderId)}/${action}`, body, true);
    }
    // ---- Stripe Terminal card payments (#1107): staff / kitchen-PIN / device token ----
    /** Starts a reader payment on a held order. 201 new, 200 replay of the same Idempotency-Key. */
    async startTerminalPayment(slug, orderId, input = {}, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/orders/${encodeURIComponent(orderId)}/terminal-payments`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Stored state only; poll every 1-2s while processing. */
    async getTerminalPayment(slug, paymentId) {
        return this.request("GET", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}`, undefined, true);
    }
    /** 409 terminal_reader_busy when the reader is mid-authorization. */
    async cancelTerminalPayment(slug, paymentId) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/cancel`, undefined, true);
    }
    async retryTerminalPayment(slug, paymentId, input = {}) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/retry`, input, true);
    }
    /** Owner or platform admin only (billing:manage). */
    async listTerminalReaders(slug) {
        const r = await this.request("GET", `${this.siteBase(slug)}/terminal/readers`, undefined, true);
        return r.readers ?? [];
    }
    // ---- Timeclock (#1203) ----
    /**
     * PIN punch (staff JWT or paired device token). Idempotent on `input.id`, so a queued
     * punch can be replayed safely. A 409 punch_sequence throws PunchSequenceError with `allowed`.
     */
    async punchClock(input) {
        try {
            return await this.request("POST", "/v1/timeclock/punch", input, true);
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 409 && err.code === "punch_sequence") {
                const body = (err.body ?? {});
                throw new PunchSequenceError(err.message, Array.isArray(body.allowed) ? body.allowed : []);
            }
            throw err;
        }
    }
    async clockStatus() {
        const r = await this.request("GET", "/v1/timeclock/status", undefined, true);
        return r.employees ?? [];
    }
    async listTimesheets(period) {
        const r = await this.request("GET", `/v1/timeclock/timesheets${ClientBase.query({ period })}`, undefined, true);
        return { period: r.period, timesheets: r.timesheets ?? [] };
    }
    async correctPunch(id, patch) {
        return this.request("PATCH", `/v1/timeclock/punches/${encodeURIComponent(id)}`, patch, true);
    }
    timeclockExportUrl(period, variant = "summary") {
        return `${this.baseUrl}/v1/timeclock/export.csv${ClientBase.query({ period, variant })}`;
    }
    async exportTimeclockCsv(period, variant = "summary") {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(this.timeclockExportUrl(period, variant), { method: "GET", headers });
        const text = await res.text();
        if (!res.ok) {
            let err = {};
            try {
                err = JSON.parse(text);
            }
            catch {
                /* non-JSON */
            }
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText);
        }
        return text;
    }
    async rollupTimesheets(period) {
        return this.request("POST", `/v1/timeclock/rollup${ClientBase.query({ period })}`, undefined, true);
    }
}
//# sourceMappingURL=dine.js.map